import { CSSProperties, useCallback, useState } from "react";
import {
  IBuilder,
  IBuilderStats,
  builderRequestError,
  useBuilderStats,
} from "../../hooks/api/admin/useBuilders";
import {
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { BaseModal } from "../shared/BaseModal";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import {
  BUILDER_STATS_PERIODS,
  BuilderStatsPeriod,
  BuilderStatus,
  builderErrorMessage,
  formatPeriodDelta,
} from "../../utils/builders";
import {
  CopyText,
  StatusBadge,
  StatusConfirmModals,
  cellStyle,
  errorStyle,
  headerCellStyle,
} from "./shared";

const TOP_MARKETS = 10;

const fieldLabelStyle: CSSProperties = {
  ...FONT_STYLE.label.three,
  color: TEXT_COLORS.two,
};

const statCardStyle: CSSProperties = {
  border: `1px solid ${BORDER_COLORS.one}`,
  borderRadius: 6,
  padding: SPACING.two,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const modalScrollStyle: CSSProperties = {
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};

function deltaColor(delta: string) {
  if (delta.startsWith("+") || delta === "new") return COLORS.green.one;
  if (delta.startsWith("-")) return COLORS.red.one;
  return TEXT_COLORS.two;
}

function StatCard({
  label, value, delta,
}: { label: string; value: string | number; delta?: string }) {
  return (
    <div style={statCardStyle} data-testid={`stat-${label}`}>
      <span style={fieldLabelStyle}>{label}</span>
      <span style={{ ...FONT_STYLE.header.six, color: TEXT_COLORS.one }}>{value}</span>
      {delta !== undefined && (
        <span style={{ ...FONT_STYLE.label.three, color: deltaColor(delta) }}>
          {delta}
          {" "}
          vs previous
        </span>
      )}
    </div>
  );
}

function StatsBody({ stats }: { stats: IBuilderStats }) {
  const { previous } = stats;
  const markets = [...(stats.markets ?? [])]
    .sort((a, b) => Number(b.volume) - Number(a.volume))
    .slice(0, TOP_MARKETS);
  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: SPACING.two,
        }}
      >
        <StatCard
          label="Volume"
          value={`$${stats.volume}`}
          delta={formatPeriodDelta(stats.volume, previous?.volume)}
        />
        <StatCard
          label="Fees"
          value={`$${stats.fees}`}
          delta={formatPeriodDelta(stats.fees, previous?.fees)}
        />
        <StatCard
          label="Active traders"
          value={stats.active_traders}
          delta={formatPeriodDelta(stats.active_traders, previous?.unique_traders)}
        />
        <StatCard
          label="Fills"
          value={stats.fills}
          delta={formatPeriodDelta(stats.fills, previous?.fills)}
        />
        <StatCard label="New traders" value={stats.new_traders} />
        <StatCard label="Returning traders" value={stats.returning_traders} />
        <StatCard label="Maker volume" value={`$${stats.maker_volume}`} />
        <StatCard label="Taker volume" value={`$${stats.taker_volume}`} />
      </div>
      <div>
        <div style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two, marginBottom: SPACING.one }}>
          Top markets
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Instrument</th>
              <th style={headerCellStyle}>Volume</th>
              <th style={headerCellStyle}>Fees</th>
              <th style={headerCellStyle}>Fills</th>
            </tr>
          </thead>
          <tbody>
            {markets.map((market) => (
              <tr key={market.instrument}>
                <td style={cellStyle}>{market.instrument}</td>
                <td style={cellStyle}>{`$${market.volume}`}</td>
                <td style={cellStyle}>{`$${market.fees}`}</td>
                <td style={cellStyle}>{market.fills}</td>
              </tr>
            ))}
            {!markets.length && (
              <tr>
                <td style={{ ...cellStyle, color: TEXT_COLORS.two }} colSpan={4}>
                  No attributed fills in this period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface IBuilderDetailModalProps {
  builder: IBuilder;
  show: boolean;
  onHide: () => void;
  // Fail closed: no status actions unless the authoritative list loaded.
  actionsDisabled: boolean;
  onSetStatus: (status: BuilderStatus) => Promise<boolean>;
}

export function BuilderDetailModal({
  builder, show, onHide, actionsDisabled, onSetStatus,
}: IBuilderDetailModalProps) {
  const [period, setPeriod] = useState<BuilderStatsPeriod>("7d");
  const [confirmStatus, setConfirmStatus] = useState<BuilderStatus>();
  const [submitting, setSubmitting] = useState(false);
  const {
    data: stats, error: statsError, isLoading: statsLoading,
  } = useBuilderStats(show ? builder.builder_id : undefined, period);

  const onConfirm = useCallback(async () => {
    if (!confirmStatus) return;
    setSubmitting(true);
    await onSetStatus(confirmStatus);
    setSubmitting(false);
    setConfirmStatus(undefined);
  }, [confirmStatus, onSetStatus]);

  const isDisabled = builder.status === "DISABLED";

  let statsContent: JSX.Element;
  if (statsError) {
    statsContent = (
      <div role="alert" style={errorStyle}>
        Unable to load stats:
        {" "}
        {builderErrorMessage(builderRequestError(statsError))}
      </div>
    );
  } else if (statsLoading || !stats) {
    statsContent = <div style={{ color: TEXT_COLORS.two }}>Loading stats…</div>;
  } else {
    statsContent = <StatsBody stats={stats} />;
  }

  return (
    <>
      <BaseModal
        show={show && !confirmStatus}
        onHide={onHide}
        title={`Builder — ${builder.name}`}
        style={{ ...modalScrollStyle, width: 760, minWidth: 760 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: SPACING.three }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "140px 1fr",
              rowGap: SPACING.one,
              alignItems: "center",
            }}
          >
            <span style={fieldLabelStyle}>Builder ID</span>
            <CopyText text={builder.builder_id} />
            <span style={fieldLabelStyle}>Name</span>
            <span>{builder.name}</span>
            <span style={fieldLabelStyle}>Fee account</span>
            <CopyText text={builder.fee_account} />
            <span style={fieldLabelStyle}>Status</span>
            <span><StatusBadge status={builder.status} /></span>
          </div>

          <div style={{ display: "flex", gap: SPACING.two, alignItems: "center" }}>
            {(builder.status === "SUSPENDED" || isDisabled) && (
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                disabled={actionsDisabled}
                onClick={() => setConfirmStatus("ACTIVE")}
                style={{ height: 30, padding: `0 ${SPACING.two}px` }}
              >
                Reactivate
              </Button>
            )}
            {(builder.status === "ACTIVE" || isDisabled) && (
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                disabled={actionsDisabled}
                onClick={() => setConfirmStatus("SUSPENDED")}
                style={{ height: 30, padding: `0 ${SPACING.two}px` }}
              >
                Suspend
              </Button>
            )}
            {!isDisabled && (
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                disabled={actionsDisabled}
                onClick={() => setConfirmStatus("DISABLED")}
                style={{ height: 30, padding: `0 ${SPACING.two}px`, color: COLORS.red.one }}
              >
                Disable
              </Button>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: SPACING.two }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={FONT_STYLE.header.six}>Attributed activity</span>
              <div role="group" aria-label="Stats period" style={{ display: "flex", gap: SPACING.one }}>
                {BUILDER_STATS_PERIODS.map((option) => (
                  <Button
                    key={option}
                    buttonTheme={option === period ? ButtonThemeEnum.NEUTRAL2 : ButtonThemeEnum.NEUTRAL3}
                    aria-pressed={option === period}
                    onClick={() => setPeriod(option)}
                    style={{ height: 28, padding: `0 ${SPACING.two}px` }}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>
            {statsContent}
          </div>
        </div>
      </BaseModal>
      <StatusConfirmModals
        show={show}
        target={confirmStatus}
        builderName={builder.name}
        submitting={submitting}
        onHide={() => setConfirmStatus(undefined)}
        onConfirm={onConfirm}
      />
    </>
  );
}
